import {
  createSlice,
  PayloadAction,
  ActionReducerMapBuilder,
} from "@reduxjs/toolkit";
import {
  fetchAssets,
  fetchAssetsByNamespace,
  fetchAsset,
  createAsset,
  updateAsset,
  deleteAsset,
  publishAsset,
  makeStableAsset,
  fetchAllNamespacesHealth,
  fetchNamespaceHealth,
  fetchAssetHealth,
  checkMemoryIntegrity,
  rebuildMemory,
} from "../actions/assetActions";
import {
  AssetHealthInfo,
  NamespaceHealthSummary,
  MemoryIntegrityCheck,
  RebuildResponse,
  AllNamespacesHealthResponse,
} from "../../types/memoryHealth";

export interface AssetContentItem {
  text: string;
  image_url?: string;
}

export interface AssetContent {
  content_id?: string;
  type: "story" | "unknown" | "faq_entry";

  // story fields
  subject?: string;
  notes?: string;
  story_items?: AssetContentItem[];

  // FAQ fields
  faq_topic?: string;
  faq_entry?: string;
  faq_media?: Record<string, any>;
  source_url?: string;
}

export interface Asset {
  id: string;
  namespace: string;
  type: "knowledge_base" | "strategy" | "presentation";
  deleted?: boolean;
  version?: number;
  published_version?: number;
  stable_version?: number;
  created_at?: string;
  deleted_at?: string | null;
  contents: AssetContent[];
  // Memory build status fields
  memory_build_status?: "idle" | "building" | "failed";
  memory_build_started_at?: string | null;
  memory_build_completed_at?: string | null;
  memory_build_error?: string | null;
}

/** Redux State */
export interface AssetsState {
  items: Asset[];
  currentAsset?: Asset;
  loading: boolean;
  error: string | null;
  
  // Memory Health State
  memoryHealth: {
    allNamespacesHealth?: AllNamespacesHealthResponse;
    namespaceHealth?: NamespaceHealthSummary;
    currentAssetHealth?: AssetHealthInfo;
    integrityCheck?: MemoryIntegrityCheck;
    rebuildStatus?: RebuildResponse;
    // Separate loading states to prevent race conditions
    allNamespacesLoading: boolean;
    namespaceHealthLoading: boolean;
    assetHealthLoading: boolean;
    integrityCheckLoading: boolean;
    rebuildLoading: boolean;
    error: string | null;
  };
}

/** Initial Redux State */
const initialState: AssetsState = {
  items: [],
  loading: false,
  error: null,
  memoryHealth: {
    allNamespacesLoading: false,
    namespaceHealthLoading: false,
    assetHealthLoading: false,
    integrityCheckLoading: false,
    rebuildLoading: false,
    error: null,
  },
};

/** Helper function to convert markdown bullets to structured items */
const parseMarkdownToItems = (markdown: string): AssetContentItem[] => {
  return markdown
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.startsWith("*"))
    .map((line) => {
      const withoutStar = line.replace(/^\*\s*/, "");
      const match = withoutStar.match(/\[\]\((.*?)\)/);
      if (match) {
        return {
          text: withoutStar.replace(`[](${match[1]})`, "").trim(),
          image_url: match[1],
        };
      }
      return { text: withoutStar };
    });
};

/** Convert DBAsset to structured Asset */
const transformDBAsset = (dbAsset: any): Asset => ({
  id: dbAsset.id,
  namespace: dbAsset.namespace,
  type: dbAsset.type,
  deleted: dbAsset.deleted || false,
  version: dbAsset.version || 1,
  published_version: dbAsset.published_version || 0,
  stable_version: dbAsset.stable_version || 0,
  created_at: dbAsset.created_at,
  deleted_at: dbAsset.deleted_at,
  // Add memory build status fields
  memory_build_status: dbAsset.memory_build_status || 'idle',
  memory_build_started_at: dbAsset.memory_build_started_at || null,
  memory_build_completed_at: dbAsset.memory_build_completed_at || null,
  memory_build_error: dbAsset.memory_build_error || null,
  contents: dbAsset.contents.map((content: any) => {
    if (content.type === "story") {
      // If it's a 'story', parse subject and markdown -> story_items
      return {
        content_id: content.content_id,
        type: "story",
        subject: content.story?.subject || "",
        notes: content.story?.notes || "",
        story_items: content.story?.markdown
          ? parseMarkdownToItems(content.story.markdown)
          : [],
        faq_topic: "",
        faq_entry: "",
      };
    } else if (content.type === "faq_entry") {
      // If it's a 'faq_entry', parse topic, entry, media, and source_url
      return {
        content_id: content.content_id,
        type: "faq_entry",
        subject: "", // not used for FAQ
        story_items: [], // not used for FAQ
        notes: "", // not used for FAQ
        faq_topic: content.faq_entry?.topic || "",
        faq_entry: content.faq_entry?.entry || "",
        faq_media: content.faq_entry?.media || undefined,
        source_url: content.faq_entry?.source || undefined,
      };
    } else {
      // Unknown or other type
      return {
        content_id: content.content_id,
        type: "unknown",
        subject: "",
        notes: "",
        story_items: [],
        faq_topic: "",
        faq_entry: "",
      };
    }
  }),
});

const assetsSlice = createSlice({
  name: "assets",
  initialState,
  reducers: {
    clearCurrentAsset(state) {
      state.currentAsset = undefined;
    },
    clearMemoryHealth(state) {
      state.memoryHealth = {
        allNamespacesLoading: false,
        namespaceHealthLoading: false,
        assetHealthLoading: false,
        integrityCheckLoading: false,
        rebuildLoading: false,
        error: null,
      };
    },
    clearNamespaceHealth(state) {
      state.memoryHealth.namespaceHealth = undefined;
      state.memoryHealth.integrityCheck = undefined;
      state.memoryHealth.namespaceHealthLoading = false;
      state.memoryHealth.integrityCheckLoading = false;
      state.memoryHealth.error = null;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<AssetsState>) => {
    builder.addCase(fetchAssets.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      fetchAssets.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.items = action.payload.map(transformDBAsset);
      }
    );
    builder.addCase(fetchAssets.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to fetch assets.";
    });

    // Handle fetchAssetsByNamespace
    builder.addCase(fetchAssetsByNamespace.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      fetchAssetsByNamespace.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.items = action.payload.map(transformDBAsset);
      }
    );
    builder.addCase(fetchAssetsByNamespace.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to fetch assets by namespace.";
    });

    builder.addCase(fetchAsset.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.currentAsset = undefined;
    });
    builder.addCase(
      fetchAsset.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.currentAsset = transformDBAsset(action.payload);
      }
    );
    builder.addCase(fetchAsset.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to fetch asset.";
    });

    builder.addCase(
      createAsset.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.items.push(transformDBAsset(action.payload));
      }
    );

    builder.addCase(
      updateAsset.fulfilled,
      (state, action: PayloadAction<any>) => {
        const updated = transformDBAsset(action.payload);
        const index = state.items.findIndex(
          (a) => a.id === updated.id && a.namespace === updated.namespace
        );
        if (index >= 0) state.items[index] = updated;
        if (state.currentAsset?.id === updated.id) state.currentAsset = updated;
      }
    );

    builder.addCase(
      deleteAsset.fulfilled,
      (state, action: PayloadAction<any>) => {
        const deleted = transformDBAsset(action.payload);
        const index = state.items.findIndex(
          (a) => a.id === deleted.id && a.namespace === deleted.namespace
        );
        if (index >= 0) state.items[index] = deleted;
        if (state.currentAsset?.id === deleted.id) state.currentAsset = deleted;
      }
    );

    builder.addCase(
      publishAsset.fulfilled,
      (state, action: PayloadAction<any>) => {
        const updated = transformDBAsset(action.payload);
        const index = state.items.findIndex(
          (a) => a.id === updated.id && a.namespace === updated.namespace
        );
        if (index >= 0) state.items[index] = updated;
        if (state.currentAsset?.id === updated.id) state.currentAsset = updated;
      }
    );

    builder.addCase(
      makeStableAsset.fulfilled,
      (state, action: PayloadAction<any>) => {
        const updated = transformDBAsset(action.payload);
        const index = state.items.findIndex(
          (a) => a.id === updated.id && a.namespace === updated.namespace
        );
        if (index >= 0) state.items[index] = updated;
        if (state.currentAsset?.id === updated.id) state.currentAsset = updated;
      }
    );

    // ============= Memory Health Reducers =============
    
    // Fetch all namespaces health
    builder.addCase(fetchAllNamespacesHealth.pending, (state) => {
      state.memoryHealth.allNamespacesLoading = true;
      state.memoryHealth.error = null;
    });
    builder.addCase(
      fetchAllNamespacesHealth.fulfilled,
      (state, action: PayloadAction<AllNamespacesHealthResponse>) => {
        state.memoryHealth.allNamespacesLoading = false;
        state.memoryHealth.allNamespacesHealth = action.payload;
      }
    );
    builder.addCase(fetchAllNamespacesHealth.rejected, (state, action) => {
      state.memoryHealth.allNamespacesLoading = false;
      state.memoryHealth.error = action.error.message || "Failed to fetch all namespaces health.";
    });
    
    // Fetch namespace health
    builder.addCase(fetchNamespaceHealth.pending, (state) => {
      state.memoryHealth.namespaceHealthLoading = true;
      state.memoryHealth.error = null;
      // Clear previous data to prevent stale state
      state.memoryHealth.namespaceHealth = undefined;
    });
    builder.addCase(
      fetchNamespaceHealth.fulfilled,
      (state, action: PayloadAction<NamespaceHealthSummary>) => {
        state.memoryHealth.namespaceHealthLoading = false;
        state.memoryHealth.namespaceHealth = action.payload;
      }
    );
    builder.addCase(fetchNamespaceHealth.rejected, (state, action) => {
      state.memoryHealth.namespaceHealthLoading = false;
      state.memoryHealth.error = action.error.message || "Failed to fetch namespace health.";
    });

    // Fetch asset health
    builder.addCase(fetchAssetHealth.pending, (state) => {
      state.memoryHealth.assetHealthLoading = true;
      state.memoryHealth.error = null;
    });
    builder.addCase(
      fetchAssetHealth.fulfilled,
      (state, action: PayloadAction<AssetHealthInfo>) => {
        state.memoryHealth.assetHealthLoading = false;
        state.memoryHealth.currentAssetHealth = action.payload;
      }
    );
    builder.addCase(fetchAssetHealth.rejected, (state, action) => {
      state.memoryHealth.assetHealthLoading = false;
      state.memoryHealth.error = action.error.message || "Failed to fetch asset health.";
    });

    // Check memory integrity
    builder.addCase(checkMemoryIntegrity.pending, (state) => {
      state.memoryHealth.integrityCheckLoading = true;
      state.memoryHealth.error = null;
    });
    builder.addCase(
      checkMemoryIntegrity.fulfilled,
      (state, action: PayloadAction<MemoryIntegrityCheck>) => {
        state.memoryHealth.integrityCheckLoading = false;
        state.memoryHealth.integrityCheck = action.payload;
      }
    );
    builder.addCase(checkMemoryIntegrity.rejected, (state, action) => {
      state.memoryHealth.integrityCheckLoading = false;
      state.memoryHealth.error = action.error.message || "Failed to check memory integrity.";
    });

    // Rebuild memory
    builder.addCase(rebuildMemory.pending, (state) => {
      state.memoryHealth.rebuildLoading = true;
      state.memoryHealth.error = null;
      state.memoryHealth.rebuildStatus = undefined;
    });
    builder.addCase(
      rebuildMemory.fulfilled,
      (state, action: PayloadAction<RebuildResponse>) => {
        state.memoryHealth.rebuildLoading = false;
        state.memoryHealth.rebuildStatus = action.payload;
      }
    );
    builder.addCase(rebuildMemory.rejected, (state, action) => {
      state.memoryHealth.rebuildLoading = false;
      state.memoryHealth.error = action.error.message || "Failed to rebuild memory.";
    });
  },
});

export const { clearCurrentAsset, clearMemoryHealth, clearNamespaceHealth } = assetsSlice.actions;
export default assetsSlice.reducer;
